import React, { useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api from "../utils/api";
import { motion } from "framer-motion";
import { FaBalanceScale } from "react-icons/fa";

/* =========================
  Helpers
========================= */
const normalizePhone = (value) => String(value || "").replace(/\D/g, "");
const isValidEgyptPhone = (phone) => /^01[0125][0-9]{8}$/.test(String(phone || ""));

export default function ForgotPassword() {
  const [phone, setPhone] = useState("");
  const [question, setQuestion] = useState("");

  const [answer, setAnswer] = useState("");
  const [newPassword, setNewPassword] = useState("");

  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState(null); // { type, text }

  const navigate = useNavigate();
  const normalizedPhone = useMemo(() => normalizePhone(phone), [phone]);

  const fetchQuestion = async () => {
    const cleanPhone = normalizePhone(phone);

    if (!isValidEgyptPhone(cleanPhone)) {
      setMessage({ type: "warning", text: "⚠️ رقم الموبايل غير صحيح (مثال: 01012345678)" });
      return;
    }

    try {
      setLoading(true);
      setMessage(null);

      const res = await api.post("/auth/get-security-question", { phone: cleanPhone });
      const data = res?.data || {};

      if (!data?.hasQuestion) {
        setQuestion("");
        setMessage({
          type: "error",
          text: "⚠️ لا يوجد سؤال أمان لهذا الحساب أو لم يتم إعداده بعد.",
        });
        return;
      }

      setQuestion(data?.question || "اسم والد والدتك");
      setMessage({ type: "success", text: "✅ تم جلب سؤال الأمان" });
    } catch (err) {
      console.error("❌ get-security-question error:", err);
      const msg =
        err?.response?.data?.message ||
        err?.message ||
        "⚠️ حصل خطأ أثناء جلب سؤال الأمان";

      setMessage({ type: "error", text: msg });
    } finally {
      setLoading(false);
    }
  };

  const resetPassword = async () => {
    const cleanPhone = normalizePhone(phone);

    if (!isValidEgyptPhone(cleanPhone)) {
      setMessage({ type: "warning", text: "⚠️ رقم الموبايل غير صحيح" });
      return;
    }

    if (!question) {
      setMessage({ type: "warning", text: "⚠️ اعرض سؤال الأمان الأول" });
      return;
    }

    if (!answer || String(answer).trim().length < 2) {
      setMessage({ type: "warning", text: "⚠️ اكتب إجابة السؤال" });
      return;
    }

    if (!newPassword || String(newPassword).length < 6) {
      setMessage({ type: "warning", text: "⚠️ كلمة المرور لازم تكون 6 أحرف على الأقل" });
      return;
    }

    try {
      setLoading(true);
      setMessage(null);

      await api.post("/auth/reset-password-by-question", {
        phone: cleanPhone,
        answer,
        newPassword,
      });

      setMessage({ type: "success", text: "✅ تم تغيير كلمة المرور بنجاح" });

      setTimeout(() => {
        navigate("/login");
      }, 700);
    } catch (err) {
      console.error("❌ reset-password-by-question error:", err);
      const msg =
        err?.response?.data?.message ||
        err?.message ||
        "⚠️ فشل تغيير كلمة المرور";

      setMessage({ type: "error", text: msg });
    } finally {
      setLoading(false);
    }
  };

  const onEnter = (e) => {
    if (e.key === "Enter") {
      if (!question) fetchQuestion();
      else resetPassword();
    }
  };

  return (
    <div
      className="flex items-center justify-center h-screen w-full bg-cover bg-center relative px-4"
      style={{
        backgroundImage:
          "url('https://images.unsplash.com/photo-1600334129128-685c5a7b4f01?auto=format&fit=crop&w=1500&q=80')",
      }}
    >
      <div className="absolute inset-0 bg-black/80" />

      <motion.div
        initial={{ opacity: 0, y: -35 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8 }}
        className="relative z-10 bg-gray-900/95 border border-yellow-500 p-8 md:p-10 rounded-2xl shadow-2xl w-full max-w-lg text-center"
      >
        <FaBalanceScale className="text-yellow-400 text-5xl mx-auto mb-4" />
        <h2 className="text-3xl font-bold text-yellow-400 mb-2">نسيت كلمة السر</h2>

        <p className="text-sm text-gray-400 mb-6">
          اكتب رقم الموبايل عشان نعرض سؤال الأمان وتقدر تغيّر كلمة المرور ⚖️
        </p>

        <div className="text-start">
          <label className="block text-xs text-gray-400 mb-1">📱 رقم الموبايل</label>
          <input
            type="tel"
            inputMode="numeric"
            placeholder="مثال: 01012345678"
            className="w-full p-3 mb-2 bg-gray-800 rounded border border-gray-700 text-white focus:outline-none focus:border-yellow-400 placeholder-gray-400"
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            onKeyDown={onEnter}
          />

          {phone && (
            <p className="text-[11px] text-gray-500 mb-3">
              {isValidEgyptPhone(normalizedPhone)
                ? `✅ الرقم مضبوط: ${normalizedPhone}`
                : `ℹ️ الرقم بعد التنظيف: ${normalizedPhone || "—"}`}
            </p>
          )}

          <button
            onClick={fetchQuestion}
            disabled={loading}
            className={`w-full py-3 font-bold rounded-lg shadow-md transition-all duration-300 ${
              loading
                ? "bg-gray-500 cursor-not-allowed text-gray-300"
                : "bg-yellow-500 hover:bg-yellow-400 text-black"
            }`}
          >
            {loading ? "⏳ جاري التحميل..." : "🧩 عرض سؤال الأمان"}
          </button>

          {question && (
            <>
              <div className="mt-5 p-3 bg-gray-800 rounded border border-gray-700 text-white">
                {question}
              </div>

              <label className="block text-xs text-gray-400 mt-4 mb-1">✍️ الإجابة</label>
              <input
                type="text"
                placeholder="اكتب إجابة السؤال"
                className="w-full p-3 mb-3 bg-gray-800 rounded border border-gray-700 text-white focus:outline-none focus:border-yellow-400 placeholder-gray-400"
                value={answer}
                onChange={(e) => setAnswer(e.target.value)}
                onKeyDown={onEnter}
              />

              <label className="block text-xs text-gray-400 mb-1">🔒 كلمة المرور الجديدة</label>
              <input
                type="password"
                placeholder="اكتب كلمة المرور الجديدة"
                className="w-full p-3 mb-4 bg-gray-800 rounded border border-gray-700 text-white focus:outline-none focus:border-yellow-400 placeholder-gray-400"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                onKeyDown={onEnter}
              />

              <button
                onClick={resetPassword}
                disabled={loading}
                className={`w-full py-3 font-bold rounded-lg shadow-md transition-all duration-300 ${
                  loading
                    ? "bg-gray-500 cursor-not-allowed text-gray-300"
                    : "bg-green-500 hover:bg-green-400 text-black"
                }`}
              >
                {loading ? "⏳ جاري الحفظ..." : "✅ تغيير كلمة المرور"}
              </button>
            </>
          )}
        </div>

        <div className="mt-6 text-sm flex items-center justify-between">
          <Link to="/login" className="text-yellow-400 hover:underline">
            الرجوع لتسجيل الدخول
          </Link>
          <Link to="/" className="text-gray-400 hover:text-yellow-300 transition">
            الرئيسية
          </Link>
        </div>

        {message && (
          <motion.div
            initial={{ opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.35 }}
            className={`mt-6 p-3 rounded-lg text-sm font-semibold ${
              message.type === "success"
                ? "bg-green-600/30 text-green-300 border border-green-500"
                : message.type === "error"
                ? "bg-red-600/30 text-red-300 border border-red-500"
                : "bg-yellow-600/30 text-yellow-300 border border-yellow-500"
            }`}
          >
            {message.text}
          </motion.div>
        )}
      </motion.div>
    </div>
  );
}
